import { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Alert, Spinner, Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  userService,
  roleService,
  User,
  CreateUserData,
  UpdateUserData,
} from '../services/user.service';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import UserFormModal from '../components/users/UserFormModal';
import UserDeleteModal from '../components/users/UserDeleteModal';
import UserDetailModal from '../components/users/UserDetailModal';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import { formatUserName } from '../utils/name.util';
import { destroyDataTableIfPresent } from '../utils/dataTableLifecycle';
// Note: DataTables CSS is loaded from local files in index.html (/vendor/datatables/css/)

export default function UsersManager() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState<number | undefined>(undefined);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);
  const dataTableRef = useRef<any>(null);
  const { user: currentUser } = useAuth();
  const isAdmin = isAdminUser(currentUser);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', page, limit, search, roleId, isActive],
    queryFn: () => userService.list({ page, limit, search, roleId, isActive }),
    enabled: isAdmin,
  });

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.list(),
    enabled: isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => userService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSuccess(t('users.created'));
      setShowFormModal(false);
      setSelectedUser(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || t('users.save_failed'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserData }) =>
      userService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSuccess(t('users.updated'));
      setShowFormModal(false);
      setSelectedUser(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || t('users.save_failed'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => userService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSuccess(t('users.deleted'));
      setShowDeleteModal(false);
      setSelectedUser(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || t('users.delete_failed'));
    },
  });

  const handleCreate = useCallback(() => {
    setSelectedUser(null);
    setShowFormModal(true);
    setError('');
  }, []);

  const handleEdit = useCallback((user: User) => {
    setSelectedUser(user);
    setShowFormModal(true);
    setError('');
  }, []);

  const handleView = useCallback((user: User) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  }, []);

  const handleDelete = useCallback((user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setError('');
  }, []);

  const handleSubmit = (data: CreateUserData | UpdateUserData) => {
    if (selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, data });
    } else {
      createMutation.mutate(data as CreateUserData);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  useEffect(() => {
    if (!tableRef.current || !data?.data) return;

    try {
      if (
        typeof window === 'undefined' ||
        !(window as any).jQuery ||
        !(window as any).jQuery.fn.DataTable
      ) {
        setError(
          t('common.error_occurred', {
            defaultValue:
              'DataTablesが読み込まれていません。jQueryとDataTablesを読み込んでください。',
          })
        );
        return;
      }

      const $ = (window as any).jQuery;

      destroyDataTableIfPresent(tableRef.current);
      dataTableRef.current = null;

      // DataTablesの言語設定を動的に変更
      const datatablesLangMap: { [key: string]: string } = {
        ja: 'ja',
        en: 'en',
        'zh-CN': 'zh-cn',
        fr: 'fr',
        es: 'es',
        de: 'de',
        pt: 'pt',
        ru: 'ru',
      };
      const datatablesLang = datatablesLangMap[i18n.language] || 'en';

      dataTableRef.current = $(tableRef.current).DataTable({
        data: data.data,
        columns: [
          {
            data: 'id',
            title: t('common.id'),
            className: 'text-center',
          },
          {
            data: 'username',
            title: t('users.col_name', { defaultValue: 'ユーザー名' }),
          },
          {
            data: 'email',
            title: t('users.col_email'),
          },
          {
            data: 'firstName',
            title: t('user.first_name', { defaultValue: '名' }),
            render: (_: any, __: any, row: User) =>
              formatUserName(row.firstName, row.lastName, i18n.language) || row.username || '-',
          },
          {
            data: 'role',
            title: t('users.col_role'),
            render: (data: any) => data?.name || '-',
          },
          {
            data: 'organizationName',
            title: t('user.organization', { defaultValue: '組織名' }),
            defaultContent: '-',
          },
          {
            data: 'isActive',
            title: t('users.col_status'),
            className: 'text-center',
            render: (data: boolean) =>
              data ? (
                <Badge bg="success">{t('users.status_active')}</Badge>
              ) : (
                <Badge bg="secondary">{t('users.status_inactive')}</Badge>
              ),
          },
          {
            data: 'lastLogin',
            title: t('users.col_last_login'),
            defaultContent: '-',
            render: (data: any) => {
              if (!data) return '-';
              try {
                return format(new Date(data), 'yyyy/MM/dd HH:mm', { locale: ja });
              } catch {
                return data;
              }
            },
          },
          {
            data: 'createdAt',
            title: t('users.col_created_at'),
            render: (data: any) => {
              if (!data) return '-';
              try {
                return format(new Date(data), 'yyyy/MM/dd HH:mm', { locale: ja });
              } catch {
                return data;
              }
            },
          },
          {
            data: null,
            title: t('users.col_actions'),
            orderable: false,
            className: 'text-center',
            defaultContent: '',
            render: (_data: any, _type: any, row: User) => {
              const isCurrentUser = row.id === currentUser?.id;
              return `
                <div class="btn-group" role="group">
                  <button class="btn btn-sm btn-info me-1 view-btn" data-id="${row.id}" title="${t('common.view', { defaultValue: '詳細' })}" aria-label="${t('common.view', { defaultValue: '詳細' })}">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="btn btn-sm btn-primary me-1 edit-btn" data-id="${row.id}" title="${t('common.edit')}" aria-label="${t('common.edit')}">
                    <i class="fas fa-edit"></i>
                  </button>
                  ${
                    !isCurrentUser
                      ? `
                    <button class="btn btn-sm btn-danger delete-btn" data-id="${row.id}" title="${t('common.delete')}" aria-label="${t('common.delete')}">
                      <i class="fas fa-trash"></i>
                    </button>
                  `
                      : ''
                  }
                </div>
              `;
            },
          },
        ],
        language: {
          url: `/vendor/datatables/i18n/${datatablesLang}.json`,
        },
        pageLength: limit,
        lengthMenu: [10, 25, 50, 100],
        order: [[0, 'desc']],
        dom: 'lBfrtip',
        buttons: [
          {
            extend: 'colvis',
            text: t('common.colvis', { defaultValue: '表示・非表示' }),
            className: 'btn btn-secondary btn-sm',
            columns: ':not(.noVis)',
            showAll: true,
            postfixButtons: ['colvisRestore'],
          },
          {
            extend: 'csv',
            text: t('common.csv', { defaultValue: 'CSV' }),
            className: 'buttons-csv',
            charset: 'utf-8',
            bom: true,
            filename: `${t('users.list_title')}_${format(new Date(), 'yyyy-MM-dd')}`,
            exportOptions: {
              columns: ':visible',
            },
          },
          {
            extend: 'excel',
            text: t('common.excel', { defaultValue: 'Excel' }),
            className: 'buttons-excel',
            title: t('users.list_title'),
            filename: `${t('users.list_title')}_${format(new Date(), 'yyyy-MM-dd')}`,
            exportOptions: {
              columns: ':visible',
            },
          },
          {
            extend: 'print',
            text: t('common.print', { defaultValue: '印刷' }),
            className: 'buttons-print',
            exportOptions: {
              columns: ':visible',
            },
          },
        ],
        responsive: true,
        autoWidth: false,
      });

      // イベントハンドラーの設定
      $(tableRef.current).on('click', '.view-btn', function (this: HTMLElement, e: JQuery.Event) {
        e.preventDefault();
        const id = $(this).data('id');
        const user = data.data.find((u: User) => u.id === id);
        if (user) {
          handleView(user);
        }
      });

      $(tableRef.current).on('click', '.edit-btn', function (this: HTMLElement, e: JQuery.Event) {
        e.preventDefault();
        const id = $(this).data('id');
        const user = data.data.find((u: User) => u.id === id);
        if (user) {
          handleEdit(user);
        }
      });

      $(tableRef.current).on('click', '.delete-btn', function (this: HTMLElement, e: JQuery.Event) {
        e.preventDefault();
        const id = $(this).data('id');
        const user = data.data.find((u: User) => u.id === id);
        if (user) {
          handleDelete(user);
        }
      });
    } catch (err) {
      console.error('DataTables初期化エラー:', err);
      setError(t('common.error_occurred', { defaultValue: 'テーブルの初期化に失敗しました' }));
    }

    return () => {
      const el = tableRef.current;
      try {
        if (el && typeof window !== 'undefined' && (window as any).jQuery) {
          const $ = (window as any).jQuery;
          $(el).off('click', '.view-btn');
          $(el).off('click', '.edit-btn');
          $(el).off('click', '.delete-btn');
        }
        destroyDataTableIfPresent(el);
      } catch (err) {
        console.error('DataTables破棄エラー:', err);
      } finally {
        dataTableRef.current = null;
      }
    };
  }, [data, limit, currentUser, handleView, handleEdit, handleDelete, t, i18n.language]);

  if (!isAdmin) {
    return (
      <Container fluid className="mt-4">
        <Alert variant="danger">
          {t('common.unauthorized', {
            defaultValue: 'このページにアクセスするには管理者権限が必要です。',
          })}
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid className="mt-4">
        <h1>{t('users.title')}</h1>
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="mt-4">
        <h1>{t('users.title')}</h1>
        <Alert variant="danger" className="mt-4">
          {t('users.fetch_failed')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('users.title')}</h1>
        <Button variant="primary" onClick={handleCreate}>
          {t('users.new_registration')}
        </Button>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 使い方の説明 */}
      <CollapsibleHelpSection
        titleKey="users.how_to_use_title"
        descriptionKey="users.how_to_use_desc"
        stepsKey={['users.how_to_use_step1', 'users.how_to_use_step2', 'users.how_to_use_step3']}
        noteKey="users.admin_only_note"
        storageKey="users_manager"
      />

      {/* フィルター */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="mb-0">
            {t('common.filter', { defaultValue: 'フィルター' })}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="users-search-keyword">{t('users.search_keyword')}</Form.Label>
                <Form.Control
                  id="users-search-keyword"
                  name="searchKeyword"
                  type="text"
                  placeholder={t('users.search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="users-filter-role">{t('users.role')}</Form.Label>
                <Form.Select
                  id="users-filter-role"
                  name="filterRole"
                  value={roleId || ''}
                  onChange={(e) => setRoleId(e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                  {rolesData?.data.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="users-filter-status">{t('users.status')}</Form.Label>
                <Form.Select
                  id="users-filter-status"
                  name="usersFilterStatus"
                  value={isActive === undefined ? '' : isActive ? 'true' : 'false'}
                  onChange={(e) =>
                    setIsActive(e.target.value === '' ? undefined : e.target.value === 'true')
                  }
                >
                  <option value="">{t('common.all', { defaultValue: 'すべて' })}</option>
                  <option value="true">{t('users.status_active')}</option>
                  <option value="false">{t('users.status_inactive')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={() => {
                  setPage(1);
                  queryClient.invalidateQueries({ queryKey: ['users'] });
                }}
              >
                {t('common.search')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* データテーブル */}
      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-hover table-bordered"
          style={{ width: '100%' }}
        >
          <thead></thead>
          <tbody></tbody>
        </table>
      </div>

      {data && (
        <div className="mt-3 text-muted">
          {t('common.total', { defaultValue: '総件数' })}: {data.pagination.total}
          {t('common.unit_items', { defaultValue: '件' })}
        </div>
      )}

      {/* フォームモーダル */}
      <UserFormModal
        show={showFormModal}
        onHide={() => {
          setShowFormModal(false);
          setSelectedUser(null);
          setError('');
        }}
        user={selectedUser}
        roles={rolesData?.data || []}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* 詳細表示モーダル */}
      <UserDetailModal
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      {/* 削除確認モーダル */}
      <UserDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
          setError('');
        }}
        user={selectedUser}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </Container>
  );
}
